import React, { useEffect, useState } from 'react';
import { useQuery } from '@edreamsodigeo/frontend-api-client';

import { useSearchItineraryQuery } from '../SearchRequestProvider';
import { SEARCH_ITINERARY_QUERY } from '../operations/searchItineraryQuery';
import {
  useTrackErrors,
  useUpdateMeshTransactionId,
  useLoadingResultsPage,
  useSkipQueryMetric,
} from './hooks';
import FrontendAPIErrorManager from 'clientState/errorManagement';
import {
  SearchItineraryQuery,
  SearchItineraryQueryVariables,
  SearchRequest,
} from 'generated/graphql';
import { useEndSearchMetricEffect } from '../SearchTrackerProvider';
import { useFrontendApiBlockingError } from '@edreamsodigeo/frontend-core';
import { transformSuccessQuery } from '../transformSuccessQuery';
import { Extensions } from 'clientState/resolvers/itinerary';
import { useAlternativeItineraryMutation } from 'clientState/providers/AlternativeItineraryMutationProvider';
import { useHash } from 'clientState/hooks/useUrlParams';
import { SearchResponseProviderProps } from './types';
import {
  SearchResponseContext,
  SearchResponseActionsContext,
  replaceItinerariesById,
} from './index';
import { transformSearchItineraryResponse } from './helpers/transformSearchItineraryResponse';
import { transformSearchItineraryUnbundleResponse } from './helpers/transformSearchItineraryUnbundleResponse';
import { useSelectedUnbundleDepartureId } from '../SelectedUnbundleDepartureProvider';
import {
  useFrontendApiValidationError,
  VALIDATION_SEARCH_ERROR,
} from 'clientState/hooks/useFrontendApiValidationError/useFrontendApiValidationError';
import { UnbundleContext } from './SearchItinerariesResponseProvider';

const SearchItinerariesResponseProviderWithUnbundle: React.FC<
  SearchResponseProviderProps & {
    isUnbundleEnabled?: boolean;
  }
> = ({
  onNoSearchResults,
  onSearchParamsError,
  children,
  onLoadingResultsPage,
  isUnbundleEnabled = false,
}) => {
  const searchRequest = useSearchItineraryQuery();
  const hash = useHash();
  const isUnbundleState = useState(false);
  // This is true if unbundle is enabled, it's round trip and it's mobile
  const selectedDepartureId = useSelectedUnbundleDepartureId();
  const [isUnbundle, setIsUnbundle] = isUnbundleState;
  const {
    alternativeMutationLaunched,
    alternativeSearchId,
    alternativeItineraries,
  } = useAlternativeItineraryMutation();
  const isNotAllowedHash =
    hash &&
    !(hash.includes('#results') || hash.includes('#/results')) &&
    isUnbundle &&
    hash.includes('step=return');
  const skipQuery =
    searchRequest === null ||
    alternativeMutationLaunched ||
    alternativeSearchId !== null ||
    !!isNotAllowedHash;
  useSkipQueryMetric(
    skipQuery,
    searchRequest,
    alternativeMutationLaunched,
    alternativeSearchId,
    isNotAllowedHash
  );
  const queryItinerarySearchResult = useQuery<
    SearchItineraryQuery,
    SearchItineraryQueryVariables
  >(SEARCH_ITINERARY_QUERY, {
    variables: {
      searchItineraryRequest: searchRequest as SearchRequest,
    },
    skip: skipQuery,
    notifyOnBackgroundFetching: true,
  });
  // Handles any broken flow error from the back-end, but not the validation
  // errors
  useFrontendApiBlockingError(queryItinerarySearchResult, [
    VALIDATION_SEARCH_ERROR,
  ]);
  // Handles the validation errors
  useFrontendApiValidationError(queryItinerarySearchResult);

  const itineraries =
    queryItinerarySearchResult.data?.searchItinerary?.itineraries || [];
  const itinerariesWithAlternatives = replaceItinerariesById(
    itineraries,
    alternativeItineraries
  );

  if (!isUnbundle && queryItinerarySearchResult.data) {
    queryItinerarySearchResult.data.searchItinerary.itineraries =
      itinerariesWithAlternatives;
  }

  // This use effect is to update isUnbundle flag once we receive
  // the value of the searchRequest
  useEffect(() => {
    setIsUnbundle(
      isUnbundleEnabled && searchRequest?.tripType === 'ROUND_TRIP'
    );
  }, [setIsUnbundle, isUnbundleEnabled, searchRequest]);

  const searchQueryState = transformSuccessQuery(
    {
      loading:
        queryItinerarySearchResult.loading ||
        queryItinerarySearchResult.fetching,
      error: queryItinerarySearchResult.errors,
      data: queryItinerarySearchResult.data,
      extensions: queryItinerarySearchResult.extensions,
    },
    isUnbundle
      ? transformSearchItineraryUnbundleResponse
      : transformSearchItineraryResponse,
    {
      selectedDepartureId,
    }
  );
  const { loading: loadingSearch, data, error } = searchQueryState;

  const loading =
    loadingSearch ||
    searchRequest === null ||
    queryItinerarySearchResult.fetching ||
    (!error && queryItinerarySearchResult.data === null);
  const extensions = searchQueryState?.extensions;

  useEndSearchMetricEffect(queryItinerarySearchResult);
  useTrackErrors(data, loading, queryItinerarySearchResult.errors);
  useUpdateMeshTransactionId(data);
  useLoadingResultsPage(loading, onLoadingResultsPage);
  const nextQueryState = React.useMemo(
    () => ({
      loading,
      data: loading ? null : data,
      error,
      extensions: extensions as Extensions | null,
    }),
    [loading, data, error, extensions]
  );

  const actions = {
    onNoSearchResults,
    onSearchParamsError,
  };

  return (
    <>
      <FrontendAPIErrorManager
        errors={queryItinerarySearchResult.errors}
        id="SearchResponseProvider"
      />
      <SearchResponseContext.Provider value={nextQueryState}>
        <SearchResponseActionsContext.Provider value={actions}>
          <UnbundleContext.Provider
            value={{ isUnbundle, isUnbundlePricePerSegment: false }}
          >
            {children}
          </UnbundleContext.Provider>
        </SearchResponseActionsContext.Provider>
      </SearchResponseContext.Provider>
    </>
  );
};

export default SearchItinerariesResponseProviderWithUnbundle;
